import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FirebasePatientService, Patient } from '../../../services/firebase/FirebasePatientService';
import { FirebaseFormAssignmentService } from '../../../services/firebase/FirebaseFormAssignmentService';
import { FirebaseCoupleService, CoupleRecord } from '../../../services/firebase/FirebaseCoupleService';
import TeamMemberService, { TeamMember } from '../../../services/TeamMemberService';
import RecentlyViewedService from '../../../services/RecentlyViewedService';
import ClientProfile from './ClientProfile';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../Toast';
import { useConfirm } from '../../../hooks/useConfirm';

interface ClientsTabProps {
  onStartSession?: (patient: any) => void;
  onScheduleAppointment?: () => void;
  onMessageClient?: (client: any) => void;
}

const ClientsTab: React.FC<ClientsTabProps> = ({ onStartSession, onScheduleAppointment, onMessageClient }) => {
  const { user, roles } = useAuth();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const providerId = user?.uid || '';

  // System Admin, Clinical Director, and Administrative Staff see ALL patients in the practice
  const canSeeAllPatients = roles.includes('system_admin') || roles.includes('clinical_director') || roles.includes('administrative_staff');

  // Patients State (Firestore)
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(true);

  // Unassigned Patients (claimable)
  const [unassignedPatients, setUnassignedPatients] = useState<Patient[]>([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [claimingPatientId, setClaimingPatientId] = useState<string | null>(null);

  // Team Members State
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const teamMemberService = TeamMemberService.getInstance();

  // Recently Viewed Service
  const recentlyViewedService = RecentlyViewedService.getInstance();

  // Patient Management State
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(true); // Quick Add = minimal fields for digitizing paper charts
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('All providers');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [showPatientActions, setShowPatientActions] = useState<string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const actionButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  // Provider Reassignment Modal State
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignPatient, setReassignPatient] = useState<any>(null);
  const [newProviderId, setNewProviderId] = useState('');

  // Client Profile State
  const [selectedClient, setSelectedClient] = useState<Patient | null>(null);

  // Couples State
  const [couples, setCouples] = useState<CoupleRecord[]>([]);
  const [showCoupleModal, setShowCoupleModal] = useState(false);
  const [coupleTargetPatient, setCoupleTargetPatient] = useState<Patient | null>(null);
  const [coupleSearch, setCoupleSearch] = useState('');
  const [coupleRelationshipType, setCoupleRelationshipType] = useState<CoupleRecord['relationshipType']>('partnered');
  const [creatingCouple, setCreatingCouple] = useState(false);
  const [showCouplesView, setShowCouplesView] = useState(false);

  // Load patients from Firestore — admins see all, providers see own
  const loadPatients = async () => {
    if (!providerId) return;
    setLoadingPatients(true);
    try {
      const data = canSeeAllPatients
        ? await FirebasePatientService.getAllPracticePatients()
        : await FirebasePatientService.getAllPatients(providerId);
      setPatients(data);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Load unassigned patients (those with no provider)
  const loadUnassignedPatients = async () => {
    setLoadingUnassigned(true);
    try {
      const data = await FirebasePatientService.getUnassignedPatients();
      setUnassignedPatients(data);
    } catch (error) {
      console.error('Error loading unassigned patients:', error);
    } finally {
      setLoadingUnassigned(false);
    }
  };

  // Claim an unassigned patient
  const claimPatient = async (patient: Patient) => {
    if (!providerId) return;
    setClaimingPatientId(patient.id);
    try {
      await FirebasePatientService.updatePatient(patient.id, { assignedProviderId: providerId });
      await FirebaseFormAssignmentService.assignStandardForms(patient.id, providerId);
      // Refresh both lists
      await Promise.all([loadPatients(), loadUnassignedPatients()]);
    } catch (error) {
      console.error('Error claiming patient:', error);
    } finally {
      setClaimingPatientId(null);
    }
  };

  useEffect(() => {
    setTeamMembers(teamMemberService.getAllTeamMembers());
    loadPatients();
    loadUnassignedPatients();

    // Subscribe to real-time Firestore updates for team members
    const handleTeamUpdate = () => {
      setTeamMembers(teamMemberService.getAllTeamMembers());
    };
    teamMemberService.subscribe(handleTeamUpdate);
    return () => teamMemberService.unsubscribe(handleTeamUpdate);
  }, [providerId]);

  // Subscribe to couples — query by patient IDs so transferred clients' couples still show
  useEffect(() => {
    if (!providerId || patients.length === 0) return;
    const patientIds = patients.map(p => p.id);
    const unsub = FirebaseCoupleService.subscribeToCouplesByPatients(patientIds, (data) => {
      setCouples(data);
    });
    return () => unsub();
  }, [providerId, patients]);

  // Couple helpers
  const getCoupleForPatient = (patientId: string): CoupleRecord | undefined => {
    return couples.find(c =>
      c.status === 'active' && (c.patient1Id === patientId || c.patient2Id === patientId)
    );
  };

  const handleAddToCouple = (patient: Patient) => {
    setCoupleTargetPatient(patient);
    setCoupleSearch('');
    setCoupleRelationshipType('partnered');
    setShowCoupleModal(true);
    setShowPatientActions(null);
    setActionMenuPosition(null);
  };

  const createCoupleRecord = async (partner: Patient) => {
    if (!coupleTargetPatient || !providerId) return;
    setCreatingCouple(true);
    try {
      await FirebaseCoupleService.createCouple({
        patient1Id: coupleTargetPatient.id,
        patient2Id: partner.id,
        patient1Name: `${coupleTargetPatient.firstName} ${coupleTargetPatient.lastName}`,
        patient2Name: `${partner.firstName} ${partner.lastName}`,
        providerId,
        providerName: user?.displayName || '',
        relationshipType: coupleRelationshipType,
        status: 'active',
      });
      setShowCoupleModal(false);
      setCoupleTargetPatient(null);
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to create couple record.');
    } finally {
      setCreatingCouple(false);
    }
  };

  const handleDissolveCouple = async (coupleId: string) => {
    const confirmed = await confirm({ title: 'Dissolve Couple', message: 'Are you sure you want to dissolve this couple? Individual client records will be preserved.', confirmLabel: 'Dissolve', variant: 'danger' });
    if (!confirmed) return;
    try {
      await FirebaseCoupleService.dissolveCouple(coupleId, providerId);
    } catch (error) {
      console.error('Error dissolving couple:', error);
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showPatientActions) {
        const target = event.target as HTMLElement;
        const isClickInsideMenu = target.closest('[data-action-menu]');
        const isClickOnButton = target.closest('[data-action-button]');
        if (!isClickInsideMenu && !isClickOnButton) {
          setShowPatientActions(null);
          setActionMenuPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPatientActions]);

  // Patient Management Functions (Firestore)
  const addPatient = async (patientData: any) => {
    try {
      await FirebasePatientService.createPatient({
        ...patientData,
        assignedProviderId: patientData.assignedProviderId || providerId,
        status: patientData.status || 'active',
      });
      await loadPatients();
      setShowAddPatientModal(false);
    } catch (error) {
      console.error('Error adding patient:', error);
    }
  };

  const editPatient = async (id: string, updatedData: any) => {
    try {
      await FirebasePatientService.updatePatient(id, updatedData);
      await loadPatients();
      setEditingPatient(null);
    } catch (error) {
      console.error('Error updating patient:', error);
    }
  };

  const assignProviderToPatient = async (patientId: string, newProvider: string, oldProvider?: string) => {
    try {
      if (oldProvider && oldProvider !== newProvider) {
        // Full transfer: migrate all clinical records to new provider
        const result = await FirebasePatientService.transferPatientToProvider(
          patientId, oldProvider, newProvider
        );
        toast.success(`Patient transferred successfully. ${result.updatedCount} clinical records migrated.`);
      } else {
        // Initial assignment (no old provider) — just set the field
        await FirebasePatientService.updatePatient(patientId, { assignedProviderId: newProvider });
        toast.success('Provider assigned successfully.');
      }
      await loadPatients();
    } catch (error) {
      console.error('Error assigning provider:', error);
      toast.error('Failed to transfer patient. Please try again.');
    }
  };

  const handleReassignProvider = (patient: any) => {
    setReassignPatient(patient);
    setNewProviderId(patient.assignedProviderId || '');
    setShowReassignModal(true);
    setShowPatientActions(null);
  };

  const confirmReassignProvider = () => {
    if (reassignPatient && newProviderId) {
      assignProviderToPatient(reassignPatient.id, newProviderId, reassignPatient.assignedProviderId);
    }
    setShowReassignModal(false);
    setReassignPatient(null);
    setNewProviderId('');
  };

  // If a client is selected, show their profile
  if (selectedClient) {
    return (
      <ClientProfile
        client={selectedClient}
        onBack={() => setSelectedClient(null)}
        onStartSession={onStartSession}
        onScheduleAppointment={onScheduleAppointment}
        onMessageClient={onMessageClient}
      />
    );
  }

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#16325B',
      minHeight: '100vh',
      color: '#f9fafb'
    }}>
      {/* Header Section */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#F7E9A0',
            margin: '0 0 8px 0'
          }}>
            Clients
          </h1>
          <p style={{
            fontSize: '16px',
            color: '#e5e7eb',
            margin: '0 0 16px 0'
          }}>
            Manage clients and assign providers
          </p>
          <span style={{
            color: '#d1d5db',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            Use the tabs below to view, add, and manage your clients.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            color: '#f9fafb',
            fontSize: '14px',
            cursor: 'pointer'
          }}>
            Export client list
          </button>
          <button
            onClick={() => setShowAddPatientModal(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: '#D4A017',
              border: 'none',
              borderRadius: '6px',
              color: '#113F67',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Add client
          </button>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
        border: '1px solid #6CA6C1'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          alignItems: 'end'
        }}>
          <div>
            <label htmlFor="clients-search" style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#f9fafb',
              marginBottom: '6px'
            }}>
              Search clients
            </label>
            <input
              id="clients-search"
              type="text"
              placeholder="Search by name or email..."
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              aria-label="Search clients by name or email"
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            />
          </div>
          <div>
            <label htmlFor="clients-provider-filter" style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#f9fafb',
              marginBottom: '6px'
            }}>
              Filter by provider
            </label>
            <select
              id="clients-provider-filter"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              aria-label="Filter clients by provider"
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            >
              <option value="All providers">All providers</option>
              <option value="Unassigned">Unassigned</option>
              {teamMembers
                .filter(member => ['Provider (Clinician)', 'Clinical Director', 'System Administrator'].includes(member.role))
                .filter(member => member.authUid)
                .map(member => (
                <option key={member.id} value={member.authUid}>{member.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="clients-status-filter" style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#f9fafb',
              marginBottom: '6px'
            }}>
              Status
            </label>
            <select
              id="clients-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              aria-label="Filter clients by status"
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#113F67',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#f9fafb',
                fontSize: '14px'
              }}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Patient Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {(() => {
          const total = patients.length;
          const active = patients.filter(p => p.status === 'active').length;
          const inactive = patients.filter(p => p.status === 'inactive').length;
          const assigned = patients.filter(p => p.assignedProviderId).length;
          const activeCouples = couples.filter(c => c.status === 'active').length;
          return [
            { label: 'Total Clients', value: total, color: '#D4A017' },
            { label: 'Active', value: active, color: '#10b981' },
            { label: 'Inactive', value: inactive, color: '#f59e0b' },
            { label: 'Couples', value: activeCouples, color: '#7C3AED' },
            { label: 'Assigned', value: assigned, color: '#3b82f6' }
          ].map(stat => (
            <div key={stat.label} style={{
              backgroundColor: '#28527A',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #6CA6C1',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '32px',
                fontWeight: '700',
                color: stat.color,
                marginBottom: '8px'
              }}>
                {stat.value}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#e5e7eb',
                fontWeight: '500'
              }}>
                {stat.label}
              </div>
            </div>
          ));
        })()}
      </div>

      {/* Unassigned Clients Banner */}
      {unassignedPatients.length > 0 && (
        <div style={{
          backgroundColor: '#28527A',
          borderRadius: '8px',
          border: '1px solid #f59e0b',
          marginBottom: '16px',
          overflow: 'hidden'
        }}>
          <div style={{
            backgroundColor: '#113F67',
            padding: '12px 20px',
            borderBottom: '1px solid #f59e0b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '16px' }}>&#9888;</span>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#f59e0b',
              margin: 0
            }}>
              Unassigned Clients ({unassignedPatients.length})
            </h3>
            <span style={{ fontSize: '13px', color: '#d1d5db', marginLeft: '8px' }}>
              These clients registered without a provider. Claim them to begin care.
            </span>
          </div>

          <div>
            {unassignedPatients.map((patient, index) => (
              <div
                key={patient.id}
                style={{
                  padding: '12px 20px',
                  borderBottom: index < unassignedPatients.length - 1 ? '1px solid #6CA6C1' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: '#f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#113F67'
                  }}>
                    {`${patient.firstName} ${patient.lastName}`.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#f9fafb' }}>
                      {patient.firstName} {patient.lastName}
                    </div>
                    <div style={{ fontSize: '13px', color: '#d1d5db' }}>
                      {patient.email} {patient.phone ? `\u2022 ${patient.phone}` : ''}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => claimPatient(patient)}
                  disabled={claimingPatientId === patient.id}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: claimingPatientId === patient.id ? '#6B7280' : '#D4A017',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#113F67',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: claimingPatientId === patient.id ? 'not-allowed' : 'pointer'
                  }}
                >
                  {claimingPatientId === patient.id ? 'Claiming...' : 'Claim Client'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Couples Section */}
      {couples.filter(c => c.status === 'active').length > 0 && (
        <div style={{
          backgroundColor: '#28527A', borderRadius: '8px', border: '1px solid #7C3AED',
          marginBottom: '16px', padding: '16px'
        }}>
          <button
            onClick={() => setShowCouplesView(!showCouplesView)}
            style={{
              background: 'none', border: 'none', color: '#A78BFA', fontSize: '16px',
              fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
              padding: 0, width: '100%', textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '12px' }}>{showCouplesView ? '▼' : '▶'}</span>
            Couples ({couples.filter(c => c.status === 'active').length})
          </button>
          {showCouplesView && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {couples.filter(c => c.status === 'active').map(couple => (
                <div key={couple.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  backgroundColor: '#113F67', borderRadius: '8px', padding: '12px 16px',
                  border: '1px solid #2A5470'
                }}>
                  <div>
                    <div style={{ color: '#f9fafb', fontWeight: '500', fontSize: '15px' }}>
                      {couple.coupleLabel || `${couple.patient1Name} & ${couple.patient2Name}`}
                    </div>
                    <div style={{ color: '#d1d5db', fontSize: '13px', marginTop: '2px' }}>
                      {couple.relationshipType.charAt(0).toUpperCase() + couple.relationshipType.slice(1)} • Created {couple.createdAt.toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDissolveCouple(couple.id)}
                    style={{
                      padding: '4px 10px', backgroundColor: 'transparent',
                      border: '1px solid #6B7280', borderRadius: '4px',
                      color: '#d1d5db', fontSize: '12px', cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ef4444'; e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#6B7280'; e.currentTarget.style.color = '#9ca3af'; }}
                  >
                    Dissolve
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Patient List */}
      <div style={{
        backgroundColor: '#28527A',
        borderRadius: '8px',
        border: '1px solid #6CA6C1',
        overflow: 'visible'
      }}>
        <div style={{
          backgroundColor: '#113F67',
          padding: '16px 20px',
          borderBottom: '1px solid #6CA6C1'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#F7E9A0',
            margin: 0
          }}>
            Clients ({(() => {
              const filtered = patients.filter(patient => {
                const matchesSearch = `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
                                     patient.email.toLowerCase().includes(patientSearch.toLowerCase());
                const matchesProvider = selectedProvider === 'All providers' ||
                                       (selectedProvider === 'Unassigned' && !patient.assignedProviderId) ||
                                       patient.assignedProviderId === selectedProvider;
                const matchesStatus = statusFilter === 'all' || patient.status === statusFilter;
                return matchesSearch && matchesProvider && matchesStatus;
              });
              return filtered.length;
            })()})
          </h3>
        </div>

        <div style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'visible' }} aria-live="polite">
          {(() => {
            const filteredPatients = patients.filter(patient => {
              const matchesSearch = `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
                                   patient.email.toLowerCase().includes(patientSearch.toLowerCase());
              const matchesProvider = selectedProvider === 'All providers' ||
                                     (selectedProvider === 'Unassigned' && !patient.assignedProviderId) ||
                                     patient.assignedProviderId === selectedProvider;
              return matchesSearch && matchesProvider;
            });

            if (filteredPatients.length === 0) {
              return (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#d1d5db'
                }}>
                  {patientSearch || selectedProvider !== 'All providers' || statusFilter !== 'all'
                    ? 'No clients match your search criteria'
                    : 'No clients added yet'
                  }
                </div>
              );
            }

            return filteredPatients.map((patient, index) => {
              const assignedProviderMember = patient.assignedProviderId
                ? teamMembers.find(member => member.authUid === patient.assignedProviderId || member.id === patient.assignedProviderId)
                : null;
              // If team member lookup fails (real provider UID), use logged-in provider's name
              const assignedProviderName = assignedProviderMember?.name
                || (patient.assignedProviderId === providerId ? (user?.displayName || 'Current Provider') : null);

              return (
                <div
                  key={patient.id}
                  style={{
                    padding: '16px 20px',
                    borderBottom: index < filteredPatients.length - 1 ? '1px solid #6CA6C1' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1e3a5f';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#D4A017',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#113F67'
                      }}>
                        {`${patient.firstName} ${patient.lastName}`.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div>
                        <button
                          onClick={() => {
                            // Determine client type based on dateOfBirth
                            const birthDate = new Date(patient.dateOfBirth);
                            const age = Math.floor((new Date().getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
                            const clientType = age < 18 ? 'Minor' : 'Adult';

                            // Add to recently viewed
                            recentlyViewedService.addClient({
                              id: patient.id,
                              name: `${patient.firstName} ${patient.lastName}`,
                              phone: patient.phone,
                              type: clientType
                            });

                            setSelectedClient(patient);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#58A6FF',
                            marginBottom: '2px',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            padding: 0,
                            textAlign: 'left'
                          }}
                        >
                          {`${patient.firstName} ${patient.lastName}`}
                        </button>
                        {getCoupleForPatient(patient.id) && (
                          <span style={{
                            display: 'inline-block',
                            padding: '1px 8px',
                            borderRadius: '10px',
                            backgroundColor: '#7C3AED',
                            color: '#fff',
                            fontSize: '11px',
                            fontWeight: '500',
                            marginLeft: '8px',
                            verticalAlign: 'middle'
                          }}>
                            Couple
                          </span>
                        )}
                        <div style={{
                          fontSize: '14px',
                          color: '#d1d5db'
                        }}>
                          {patient.email} • {patient.phone}
                          {getCoupleForPatient(patient.id) && (
                            <span style={{ color: '#A78BFA', marginLeft: '8px' }}>
                              — with {FirebaseCoupleService.getPartnerName(getCoupleForPatient(patient.id)!, patient.id)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      fontSize: '13px',
                      color: '#d1d5db'
                    }}>
                      <span>DOB: {patient.dateOfBirth?.split('-').length === 3 ? `${patient.dateOfBirth.split('-')[1]}/${patient.dateOfBirth.split('-')[2]}/${patient.dateOfBirth.split('-')[0]}` : new Date(patient.dateOfBirth).toLocaleDateString('en-US')}</span>
                      {patient.intakeDate && <span>Intake: {new Date(patient.intakeDate).toLocaleDateString()}</span>}
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: patient.status === 'active' ? '#10b981' : patient.status === 'inactive' ? '#f59e0b' : '#6b7280',
                        color: patient.status === 'inactive' ? '#113F67' : '#fff',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}>
                        {patient.status}
                      </span>
                      <span
                        title={patient.isEstablished ? 'Established patient — skips initial consultation' : 'New patient — will see initial consultation option'}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          backgroundColor: patient.isEstablished ? '#6366f1' : '#374151',
                          color: patient.isEstablished ? '#fff' : '#9ca3af',
                          fontSize: '11px',
                          fontWeight: '500',
                          border: patient.isEstablished ? 'none' : '1px dashed #6b7280',
                        }}
                      >
                        {patient.isEstablished ? 'Established' : 'New'}
                      </span>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '14px',
                        color: '#f9fafb',
                        marginBottom: '2px'
                      }}>
                        {assignedProviderName ? `Assigned to: ${assignedProviderName}` : 'Unassigned'}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#d1d5db'
                      }}>
                        {patient.insurance?.provider || 'No insurance'}
                      </div>
                    </div>

                    <div style={{ position: 'relative' }}>
                      <button
                        ref={(el) => { actionButtonRefs.current[patient.id] = el; }}
                        data-action-button
                        onClick={(e) => {
                          if (showPatientActions === patient.id) {
                            setShowPatientActions(null);
                            setActionMenuPosition(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setActionMenuPosition({
                              top: rect.bottom + window.scrollY + 4,
                              right: window.innerWidth - rect.right + window.scrollX
                            });
                            setShowPatientActions(patient.id);
                          }
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: 'transparent',
                          border: '1px solid #6CA6C1',
                          borderRadius: '6px',
                          color: '#f9fafb',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Add Patient Modal */}
      {showAddPatientModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            border: '1px solid #6CA6C1',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #6CA6C1'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#F7E9A0',
                  margin: 0
                }}>
                  {quickAddMode ? 'Quick Add Client' : 'Add New Client'}
                </h2>
                <div style={{ display: 'flex', gap: '4px', background: '#113F67', borderRadius: '6px', padding: '2px' }}>
                  <button type="button" onClick={() => setQuickAddMode(true)}
                    style={{
                      padding: '4px 12px', borderRadius: '4px', border: 'none', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      background: quickAddMode ? '#D4A017' : 'transparent',
                      color: quickAddMode ? '#113F67' : '#d1d5db',
                    }}>Quick Add</button>
                  <button type="button" onClick={() => setQuickAddMode(false)}
                    style={{
                      padding: '4px 12px', borderRadius: '4px', border: 'none', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      background: !quickAddMode ? '#D4A017' : 'transparent',
                      color: !quickAddMode ? '#113F67' : '#d1d5db',
                    }}>Full Intake</button>
                </div>
              </div>
              {quickAddMode && (
                <p style={{ color: '#d1d5db', fontSize: '13px', margin: '8px 0 0 0' }}>
                  Minimal fields for digitizing paper charts. You can add details later.
                </p>
              )}
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              const patientData: any = {
                firstName: formData.get('firstName') as string,
                lastName: formData.get('lastName') as string,
                dateOfBirth: formData.get('dateOfBirth') as string,
                assignedProviderId: (formData.get('assignedProviderId') as string) || providerId,
                status: (formData.get('clientStatus') as string) || 'active',
              };
              if (quickAddMode) {
                // Quick Add: only name, DOB, status, provider — email/phone optional
                patientData.email = (formData.get('email') as string) || '';
                patientData.phone = (formData.get('phone') as string) || '';
              } else {
                // Full Intake: all fields
                patientData.email = formData.get('email') as string;
                patientData.phone = formData.get('phone') as string;
                patientData.address = formData.get('street') as string || undefined;
                patientData.city = formData.get('city') as string || undefined;
                patientData.state = formData.get('state') as string || undefined;
                patientData.zipCode = formData.get('zipCode') as string || undefined;
                patientData.insurance = {
                  provider: formData.get('insuranceProvider') as string || '',
                  policyNumber: formData.get('policyNumber') as string || '',
                  groupNumber: formData.get('groupNumber') as string || undefined,
                  subscriberName: formData.get('subscriberName') as string || undefined,
                };
                patientData.emergencyContact = {
                  name: formData.get('emergencyName') as string || '',
                  relationship: formData.get('emergencyRelationship') as string || '',
                  phone: formData.get('emergencyPhone') as string || '',
                  email: formData.get('emergencyEmail') as string || undefined,
                };
                patientData.medicalHistory = formData.get('medicalHistory') as string || undefined;
              }
              addPatient(patientData);
            }}>
              <div style={{ padding: '20px' }}>
                {/* Name fields — always shown */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label htmlFor="add-firstName" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      First Name *
                    </label>
                    <input id="add-firstName" name="firstName" type="text" required
                      style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                  </div>
                  <div>
                    <label htmlFor="add-lastName" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Last Name *
                    </label>
                    <input id="add-lastName" name="lastName" type="text" required
                      style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                  </div>
                </div>

                {/* DOB + Status — always shown */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label htmlFor="add-dob" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Date of Birth *
                    </label>
                    <input id="add-dob" name="dateOfBirth" type="date" required
                      style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                  </div>
                  <div>
                    <label htmlFor="add-status" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                      Client Status *
                    </label>
                    <select id="add-status" name="clientStatus"
                      style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Provider — always shown */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                    Assign Provider
                  </label>
                  <select name="assignedProviderId"
                    style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }}>
                    <option value="">No provider assigned</option>
                    {teamMembers
                      .filter(member => ['Provider (Clinician)', 'Clinical Director', 'System Administrator'].includes(member.role))
                      .filter(member => member.authUid)
                      .map(member => (
                      <option key={member.id} value={member.authUid}>{member.name}</option>
                    ))}
                  </select>
                </div>

                {/* Quick Add: optional email/phone */}
                {quickAddMode && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        Email <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <input name="email" type="email" placeholder="If available..."
                        style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        Phone <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                      </label>
                      <input name="phone" type="tel" placeholder="If available..."
                        style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                    </div>
                  </div>
                )}

                {/* Full Intake: email, phone, address, insurance, emergency, medical history */}
                {!quickAddMode && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label htmlFor="add-email" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                          Email *
                        </label>
                        <input id="add-email" name="email" type="email" required
                          style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                      </div>
                      <div>
                        <label htmlFor="add-phone" style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                          Phone *
                        </label>
                        <input id="add-phone" name="phone" type="tel" required
                          style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px' }} />
                      </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', color: '#e5e7eb', fontSize: '14px', fontWeight: '500' }}>
                        Medical History
                      </label>
                      <textarea name="medicalHistory" rows={3} placeholder="Enter relevant medical history..."
                        style={{ width: '100%', padding: '8px 12px', backgroundColor: '#113F67', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px', resize: 'vertical' }} />
                    </div>
                  </>
                )}
              </div>

              <div style={{
                padding: '20px',
                borderTop: '1px solid #6CA6C1',
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button type="button" onClick={() => setShowAddPatientModal(false)}
                  style={{ padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit"
                  style={{ padding: '8px 16px', backgroundColor: '#D4A017', border: 'none', borderRadius: '6px', color: '#113F67', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                  {quickAddMode ? 'Quick Add Client' : 'Add Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reassign Provider Modal */}
      {showReassignModal && reassignPatient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#28527A',
            borderRadius: '8px',
            border: '1px solid #6CA6C1',
            width: '500px',
            maxWidth: '90vw',
            padding: '0',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #6CA6C1'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#F7E9A0',
                margin: 0
              }}>
                Reassign Provider
              </h2>
            </div>

            {/* Content */}
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '16px',
                  color: '#f9fafb',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Client: {reassignPatient.firstName} {reassignPatient.lastName}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#d1d5db',
                  marginBottom: '16px'
                }}>
                  Current provider: {(() => {
                    if (!reassignPatient.assignedProviderId) return 'Unassigned';
                    const currentProvider = teamMembers.find(member => member.authUid === reassignPatient.assignedProviderId || member.id === reassignPatient.assignedProviderId);
                    return currentProvider?.name
                      || (reassignPatient.assignedProviderId === providerId ? (user?.displayName || 'Current Provider') : 'Assigned');
                  })()}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#f9fafb',
                  marginBottom: '8px'
                }}>
                  Select new provider:
                </label>
                <select
                  value={newProviderId}
                  onChange={(e) => setNewProviderId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    backgroundColor: '#113F67',
                    border: '1px solid #6CA6C1',
                    borderRadius: '6px',
                    color: '#f9fafb',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a provider...</option>
                  {teamMembers
                    .filter(member => ['Provider (Clinician)', 'Clinical Director', 'System Administrator'].includes(member.role))
                    .filter(member => member.authUid) // Only show members who have logged in (have auth UID)
                    .map(member => (
                    <option key={member.id} value={member.authUid}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #6CA6C1',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setReassignPatient(null);
                  setNewProviderId('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  border: '1px solid #6CA6C1',
                  borderRadius: '6px',
                  color: '#f9fafb',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReassignProvider}
                disabled={!newProviderId}
                style={{
                  padding: '8px 16px',
                  backgroundColor: newProviderId ? '#D4A017' : '#6B7280',
                  border: 'none',
                  borderRadius: '6px',
                  color: newProviderId ? '#113F67' : '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: newProviderId ? 'pointer' : 'not-allowed'
                }}
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Couple Modal */}
      {showCoupleModal && coupleTargetPatient && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: '#16325B', borderRadius: '12px', padding: '28px',
            border: '1px solid #6CA6C1', width: '480px', maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3 style={{ color: '#F7E9A0', margin: '0 0 8px 0', fontSize: '20px' }}>
              Add to a Couple
            </h3>
            <p style={{ color: '#d1d5db', fontSize: '14px', margin: '0 0 20px 0' }}>
              Select a partner for <strong style={{ color: '#f9fafb' }}>{coupleTargetPatient.firstName} {coupleTargetPatient.lastName}</strong>
            </p>

            {/* Relationship type */}
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="couple-relationship" style={{ display: 'block', color: '#f9fafb', fontSize: '14px', marginBottom: '6px' }}>
                Relationship type
              </label>
              <select
                id="couple-relationship"
                value={coupleRelationshipType}
                onChange={(e) => setCoupleRelationshipType(e.target.value as CoupleRecord['relationshipType'])}
                style={{
                  width: '100%', padding: '8px 12px', backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px'
                }}
              >
                <option value="married">Married</option>
                <option value="partnered">Partnered / Domestic partnership</option>
                <option value="dating">Dating</option>
                <option value="family">Family members</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Search partner */}
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="couple-search" style={{ display: 'block', color: '#f9fafb', fontSize: '14px', marginBottom: '6px' }}>
                Search for partner
              </label>
              <input
                id="couple-search"
                type="text"
                placeholder="Type a client name..."
                value={coupleSearch}
                onChange={(e) => setCoupleSearch(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px', backgroundColor: '#113F67',
                  border: '1px solid #6CA6C1', borderRadius: '6px', color: '#f9fafb', fontSize: '14px'
                }}
              />
            </div>

            {/* Eligible partners list */}
            <div style={{ maxHeight: '240px', overflow: 'auto', marginBottom: '16px' }}>
              {patients
                .filter(p =>
                  p.id !== coupleTargetPatient.id &&
                  p.status === 'active' &&
                  !getCoupleForPatient(p.id) &&
                  (coupleSearch === '' ||
                    `${p.firstName} ${p.lastName}`.toLowerCase().includes(coupleSearch.toLowerCase()))
                )
                .map(partner => (
                  <button
                    key={partner.id}
                    onClick={() => createCoupleRecord(partner)}
                    disabled={creatingCouple}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      width: '100%', padding: '10px 12px', backgroundColor: 'transparent',
                      border: '1px solid #2A5470', borderRadius: '6px', color: '#f9fafb',
                      fontSize: '14px', cursor: 'pointer', textAlign: 'left', marginBottom: '8px'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      backgroundColor: '#7C3AED', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: '#fff',
                      flexShrink: 0
                    }}>
                      {`${partner.firstName[0]}${partner.lastName[0]}`.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500' }}>{partner.firstName} {partner.lastName}</div>
                      <div style={{ fontSize: '12px', color: '#d1d5db' }}>{partner.email}</div>
                    </div>
                  </button>
                ))
              }
              {patients.filter(p =>
                p.id !== coupleTargetPatient.id && p.status === 'active' && !getCoupleForPatient(p.id) &&
                (coupleSearch === '' || `${p.firstName} ${p.lastName}`.toLowerCase().includes(coupleSearch.toLowerCase()))
              ).length === 0 && (
                <p style={{ color: '#d1d5db', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                  {coupleSearch ? 'No matching clients found.' : 'No eligible clients available.'}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowCoupleModal(false); setCoupleTargetPatient(null); }}
                style={{
                  padding: '8px 16px', backgroundColor: 'transparent',
                  border: '1px solid #6CA6C1', borderRadius: '6px',
                  color: '#f9fafb', fontSize: '14px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Menu Portal - Rendered outside scroll container */}
      {showPatientActions && actionMenuPosition && createPortal(
        <div
          data-action-menu
          style={{
            position: 'fixed',
            top: `${actionMenuPosition.top}px`,
            right: `${actionMenuPosition.right}px`,
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            zIndex: 10000,
            minWidth: '160px'
          }}
        >
          {(() => {
            const patient = patients.find(p => p.id === showPatientActions);
            if (!patient) return null;

            const hasProvider = !!patient.assignedProviderId;

            return (
              <>
                <button
                  onClick={() => {
                    setEditingPatient(patient);
                    setShowPatientActions(null);
                    setActionMenuPosition(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#f9fafb',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#28527A';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Edit client
                </button>
                <button
                  onClick={() => {
                    handleReassignProvider(patient);
                    setActionMenuPosition(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#f9fafb',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#28527A';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {hasProvider ? 'Reassign provider' : 'Assign provider'}
                </button>
                {getCoupleForPatient(patient.id) ? (
                  <button
                    onClick={() => {
                      const couple = getCoupleForPatient(patient.id);
                      if (couple) handleDissolveCouple(couple.id);
                      setShowPatientActions(null);
                      setActionMenuPosition(null);
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#A78BFA',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    Dissolve couple
                  </button>
                ) : (
                  <button
                    onClick={() => handleAddToCouple(patient)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#A78BFA',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    Add to a couple
                  </button>
                )}
                <button
                  onClick={async () => {
                    const newVal = !patient.isEstablished;
                    await FirebasePatientService.updatePatient(patient.id, { isEstablished: newVal } as any);
                    await loadPatients();
                    toast.success(
                      newVal ? 'Marked as Established' : 'Marked as New Patient',
                      newVal
                        ? `${patient.firstName} will skip initial consultation when booking.`
                        : `${patient.firstName} will be prompted for initial consultation.`
                    );
                    setShowPatientActions(null);
                    setActionMenuPosition(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#87CEEB',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {patient.isEstablished ? 'Mark as New Patient' : 'Mark as Established'}
                </button>
                <button
                  onClick={async () => {
                    const confirmed = await confirm({
                      title: patient.status === 'discharged' ? 'Reactivate Client' : 'Discharge Client',
                      message: patient.status === 'discharged'
                        ? `Reactivate ${patient.firstName} ${patient.lastName}? They will appear as an active client again.`
                        : `Discharge ${patient.firstName} ${patient.lastName}? Their records will be preserved but they will be marked as discharged.`,
                      confirmLabel: patient.status === 'discharged' ? 'Reactivate' : 'Discharge',
                      variant: patient.status === 'discharged' ? 'default' as any : 'danger'
                    });
                    if (confirmed) {
                      try {
                        const newStatus = patient.status === 'discharged' ? 'active' : 'discharged';
                        await FirebasePatientService.updatePatient(patient.id, { status: newStatus } as any);
                        await loadPatients();
                        toast.success(
                          newStatus === 'discharged' ? 'Client Discharged' : 'Client Reactivated',
                          `${patient.firstName} ${patient.lastName} has been ${newStatus === 'discharged' ? 'discharged' : 'reactivated'}.`
                        );
                      } catch (error) {
                        console.error('Error updating patient status:', error);
                      }
                    }
                    setShowPatientActions(null);
                    setActionMenuPosition(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: patient.status === 'discharged' ? '#10b981' : '#f59e0b',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#28527A'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {patient.status === 'discharged' ? 'Reactivate client' : 'Discharge client'}
                </button>
                <button
                  onClick={async () => {
                    const confirmed = await confirm({ title: 'Archive Client', message: 'Are you sure you want to archive this client? This cannot be undone.', confirmLabel: 'Archive', variant: 'danger' });
                    if (confirmed) {
                      try {
                        await FirebasePatientService.archivePatient(patient.id);
                        await loadPatients();
                      } catch (error) {
                        console.error('Error archiving patient:', error);
                      }
                    }
                    setShowPatientActions(null);
                    setActionMenuPosition(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    fontSize: '14px',
                    textAlign: 'left',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#28527A';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Archive client
                </button>
              </>
            );
          })()}
        </div>,
        document.body
      )}
      {ConfirmDialog}
    </div>
  );
};

export default ClientsTab;